import { Link } from 'react-router-dom';
import type { Movie } from '@/types';
import StarRating from './StarRating';

interface MovieCardProps {
  movie: Movie;
  variant?: 'grid' | 'list';
}

export default function MovieCard({ movie, variant = 'grid' }: MovieCardProps) {
  if (variant === 'list') {
    return (
      <Link
        to={`/detail/${movie.id}`}
        className="group flex flex-col sm:flex-row gap-4 rounded-lg bg-background-50 border border-background-200/80 p-3 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="relative w-full sm:w-32 h-44 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={movie.cover}
            alt={`${movie.title} 电影封面`}
            title={`${movie.title} · 韩国剧情电影`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute top-2 left-2 rounded-md bg-background-50/90 px-1.5 py-0.5 font-label text-[11px] font-semibold text-foreground-800">
            {movie.year}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-base font-bold text-foreground-950 group-hover:text-primary-600 transition-colors">
              {movie.title}
            </h3>
            <StarRating rating={movie.rating} />
          </div>
          <p className="mt-1 text-xs text-foreground-500">
            {movie.titleEn} · {movie.director} 导演
          </p>
          <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">{movie.summary}</p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {movie.genres.map((g) => (
              <span
                key={g}
                className="rounded-full bg-secondary-100 px-2 py-0.5 font-label text-[11px] text-secondary-900"
              >
                {g}
              </span>
            ))}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200/80 hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="relative w-full aspect-[2/3] overflow-hidden bg-background-200">
        <img
          src={movie.cover}
          alt={`${movie.title} 电影封面`}
          title={`${movie.title} · 韩国剧情电影`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <span className="absolute top-2 left-2 rounded-md bg-background-50/90 px-1.5 py-0.5 font-label text-[11px] font-semibold text-foreground-800">
          {movie.year}
        </span>
        <span className="absolute bottom-2 right-2 rounded-md bg-foreground-950/70 px-1.5 py-0.5">
          <StarRating rating={movie.rating} size="sm" />
        </span>
      </div>
      <div className="flex flex-col flex-1 p-3">
        <h3 className="text-sm font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
          {movie.title}
        </h3>
        <p className="mt-1 text-xs text-foreground-500 line-clamp-1">{movie.director} 导演</p>
        <p className="mt-2 text-xs text-foreground-600 line-clamp-2 leading-relaxed">{movie.summary}</p>
      </div>
    </Link>
  );
}